import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { hasPermission } from '../utils/permissions';

function ProtectedRoute({ element, roles, requiredPermission }) {
  const location = useLocation();
  const details = localStorage.getItem('userInfo');
  if (!details) return <Navigate to='/permission-denied' />;

  let parsed = null;
  try {
    parsed = JSON.parse(details);
  } catch (err) {
    return <Navigate to='/permission-denied' />;
  }

  const token = parsed?.token;
  if (!token) return <Navigate to='/permission-denied' />;

  const userType = (parsed.userType || parsed.role || '').toString().toLowerCase();
  const userPermissions = parsed.permissions || [];

  // Check permission-based access if requiredPermission is specified
  // This is the PRIMARY access control - if a route requires a permission, it overrides role-based access
  if (requiredPermission) {
    // requiredPermission is the permission name (e.g., 'invoice')
    // Use hasPermission to check if user has this specific permission
    const hasPermissionAccess = hasPermission(userPermissions, requiredPermission);
    if (!hasPermissionAccess) {
      return <Navigate to='/permission-denied' />;
    }
    // If permission check passes, allow access (don't fall through to role-based logic)
    return element;
  }

  // If explicit roles prop is provided, honor it (role names should match userType or aliases)
  if (roles && roles.length > 0) {
    const userRoles = new Set([userType]);
    if (userType === 'admin') userRoles.add('super_admin');
    const allowed = roles.some((r) => userRoles.has(r.toString().toLowerCase()));
    return allowed ? element : <Navigate to='/permission-denied' />;
  }

  // Default role-based path restrictions: cashier and technician should only access a small set of pages
  const path = (location.pathname || '').toLowerCase();

  if (userType === 'cashier') {
    // allowed: profile (any /profile/...), dashboard (/profile/.../dashboard), jobs (/jobs), stock (/jobsearch), invoices (/invoices), suppliers (/projects)
    const ok = path.startsWith('/profile/') || path === '/profile' || path.startsWith('/jobs') || path.startsWith('/jobsearch') || path.startsWith('/jobdetails') || path === '/dashboard' || path.startsWith('/dashboard/') || path.startsWith('/invoices') || path.startsWith('/projects') || path.startsWith('/projectdetails') || path.startsWith('/bill') || path.startsWith('/billdetails');
    return ok ? element : <Navigate to='/permission-denied' />;
  }

  if (userType === 'technician') {
    // allowed: profile, jobs, job details, and dashboard
    const ok = path.startsWith('/profile/') || path === '/profile' || path.startsWith('/jobs') || path.startsWith('/jobdetails') || path.startsWith('/jobsearch') || path === '/dashboard' || path.startsWith('/dashboard/');
    return ok ? element : <Navigate to='/permission-denied' />;
  }

  // For admin users without a requiredPermission on the route, allow access (backward compatibility)
  if (userType === 'admin') {
    return element;
  }

  // other roles - deny by default
  return <Navigate to='/permission-denied' />;
}

export default ProtectedRoute;
